import React, { useEffect } from 'react';
import { AlertCircle, X } from 'lucide-react';
import './UploadErrors.scss';

interface Props {
  /** One line per problem. Empty (or absent) renders nothing at all. */
  messages: string[];
  /** Called by the close button and by the auto-dismiss timer. */
  onDismiss: () => void;
  /**
   * Optional line explaining what *is* accepted. Shown once under the list
   * rather than repeated per file.
   */
  hint?: string;
  /** ms before the banner clears itself. Pass 0 to keep it until dismissed. */
  autoDismissMs?: number;
  /**
   * Lines to show before collapsing the rest into a count. Dropping thirty
   * unsupported files should not produce thirty lines of red.
   */
  maxVisible?: number;
}

/**
 * The one error surface every file picker in the CMS uses.
 *
 * Upload errors used to be `alert()` in some places and a permanent red line in
 * others — the permanent one stayed on screen after the user had already fixed
 * the problem, which reads as "the form is still broken". So this banner both
 * dismisses itself and is cleared by its owner as soon as a good file arrives;
 * the close button exists for the reader who is done with it sooner.
 */
const UploadErrors: React.FC<Props> = ({
  messages,
  onDismiss,
  hint,
  autoDismissMs = 10000,
  maxVisible = 5
}) => {
  // Keyed on the joined text, not the array identity: a re-render that produces
  // an equal-but-new array must not restart the countdown, while a genuinely
  // new message must.
  const signature = messages.join('|');

  useEffect(() => {
    if (!signature || !autoDismissMs) return undefined;
    const timer = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, autoDismissMs]);

  if (messages.length === 0) return null;

  const visible = messages.slice(0, maxVisible);
  const hidden = messages.length - visible.length;

  return (
    <div className="upload-errors" role="alert" aria-live="assertive">
      <AlertCircle className="upload-errors__icon" size={18} aria-hidden="true" />
      <div className="upload-errors__body">
        {visible.map((message, index) => (
          <p className="upload-errors__message" key={`${index}-${message}`}>{message}</p>
        ))}
        {hidden > 0 && (
          <p className="upload-errors__message">ועוד {hidden} קבצים שלא נוספו.</p>
        )}
        {hint && <p className="upload-errors__hint">{hint}</p>}
      </div>
      <button
        type="button"
        className="upload-errors__dismiss"
        onClick={onDismiss}
        aria-label="סגור הודעה"
        title="סגור"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default UploadErrors;
